/** The CLAIMS channel of one (island, lane) — wire contract §4 (v1.4–v1.6:
 *  subject + hash, server-judged freshness), vc-impl's routes. The same sync
 *  rule as the computed channel: open the base the open names, catch up from
 *  its `as_of`, a 410 re-opens from the base, tombstones drop.
 *
 *  An item is a whole claim record — `{id, _label, _v, island_id, entity_id,
 *  content_hash, lane, commit_id, fresh, ...its readable fields}` — and
 *  `fresh` is judged for the lane read. A commit that changes a subject sends
 *  a SLIM freshness frame, `{claims: true, items: [{id, fresh, _v}]}`, to the
 *  subscribers of the lane it was judged for; its `_v` is on the items'
 *  clock, and a since then returns the full item at that `_v`. A slim item
 *  for a claim this channel does not hold is dropped: the since brings it
 *  (vc-impl, 2026-10-08).
 *
 *  STALE CLAIMS (Tom, 2026-10-12; contract v1.9f §4): the subscription says
 *  whether stale claims are sent (`send`) or left out (`omit`), else each
 *  class's default. Under `omit` the base leaves them out and the since
 *  sends a claim that turned stale once, slim (`{id, _label, fresh: false,
 *  _v}`); one fresh again comes whole. So a claim whose rule omits stale
 *  ones is REMOVED from the store when it arrives stale by any path (base,
 *  since or a live frame), never shown as current — and a copy older than
 *  that removal does not bring it back. A deletion is still a tombstone. */

import { ComputedChannel, type ComputedChannelOptions, type ComputedItem } from "./computed-channel";

export type ClaimsFrame = { claims?: boolean; branch?: string; items?: { id: string; fresh?: boolean; _v?: number }[] };

export type ClaimsChannelOptions = ComputedChannelOptions & {
  /** whether stale claims of this class are kept (shown marked stale) */
  sendsStale?: (label: string | undefined) => boolean;
};

export class ClaimsChannel extends ComputedChannel {
  /** each claim's class, from its whole record: a slim item has none */
  private labels = new Map<string, string>();
  /** claims removed for going stale, at the `_v` they went stale */
  private hidden = new Map<string, number>();

  constructor(opts: ClaimsChannelOptions) {
    super(opts);
  }

  private keepsStale(label: string | undefined): boolean {
    return (this.opts as ClaimsChannelOptions).sendsStale?.(label) ?? true;
  }

  protected apply(items: ComputedItem[]): void {
    if (this.retired) return;
    const shown: ComputedItem[] = [];
    const removed: string[] = [];
    for (const it of items) {
      const v = typeof it._v === "number" ? it._v : -Infinity;
      const hiddenAt = this.hidden.get(it.id);
      if (hiddenAt !== undefined && hiddenAt > v) continue; // older than its removal
      const label = it._label ?? this.labels.get(it.id);
      if (label) this.labels.set(it.id, label);
      if (it.fresh === false && !this.keepsStale(label)) {
        const held = this.versions.get(it.id);
        if (held !== undefined && held > v) continue;
        this.versions.delete(it.id);
        this.hidden.set(it.id, v);
        removed.push(it.id);
        continue;
      }
      this.hidden.delete(it.id);
      shown.push(it);
    }
    super.apply(shown);
    if (removed.length) this.opts.sink.dropComputed(removed);
  }

  onFrame(frame: ClaimsFrame | any): void {
    const items = (frame?.items ?? []) as ComputedItem[];
    // a SLIM item (`{id, fresh, _v}`) updates a claim this channel holds; a
    // WHOLE record (it names its class) is applied even when it is not held:
    // a claim turning fresh again on a lane that omits stale claims was
    // removed here (or never in the base, stale at the open), and its frame
    // carries the whole record, filtered per reader (VC-D, 2026-10-13) —
    // `apply` still refuses one older than the claim's going stale
    this.apply(items.filter((it) => this.versions.has(it.id) || typeof it._label === "string"));
  }
}
